import { AxiosRequestConfig, AxiosResponse } from 'axios'

import apiClient from '@core/api/client'
import {
	MACEDONIAN_COLLECTION_ID,
	COSTA_RICA_COLLECTION_ID,
	MALAYSIA_COLLECTION_ID,
} from '@core/api/dtos/response/campaign/voice'

export const campaignService = {
	paginateCampaigns(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/my/announcements', config)
	},

	getCampaign(id: number, config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get(`/my/announcements/${id}`, config)
	},

	// Get available ElevenLabs voices directly from ElevenLabs API
	async getVoices(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		const apiKey = import.meta.env?.VITE_ELEVEN_LABS_API_KEY ?? ''

		if (!apiKey) {
			throw new Error('ELEVEN_LABS_API_KEY is not configured')
		}

		// Call ElevenLabs API directly
		const response = await fetch('https://api.elevenlabs.io/v1/voices', {
			headers: {
				'xi-api-key': apiKey,
			},
		})

		if (!response.ok) {
			throw new Error('Failed to fetch voices from ElevenLabs')
		}

		const data = await response.json()

		// Apply filters as per backend code
		const filteredVoices =
			data.voices?.filter((voice: any) => {
				// Drop incompatible voices
				if (
					[
						// British
						'JBFqnCBsd6RMkjVDRZzb', // George
						'Xb7hH8MSUJpSbSDYk0k2', // Alice
						'onwK4e9ZLuTAKqWW03F9', // Daniel
						'pFZP5JQG7iQjIQuC4Bku', // Lily
						// American
						'EXAVITQu4vr4xnSDxMaL', // Sarah
						'TX3LPaxmHKxFdv7VOQHJ', // Liam
						'XrExE9yKIg1WjnnlVkGX', // Matilda
						'iP95p4xoKVk53GoZ742B', // Chris
						'nPczCjzI2devNBz1zQrb', // Brian
						'pqHfZKP75CvOlQylNhV4', // Bill
						'gUABw7pXQjhjt0kNFBTF', // Andrew
					].includes(voice.voice_id)
				) {
					return false
				}

				// If high_quality_base_model_ids is empty, remove the voice
				// Exception: Allow voices in Macedonian or Costa Rica collections to pass through
				if (
					!voice.high_quality_base_model_ids ||
					voice.high_quality_base_model_ids.length === 0
				) {
					// Allow voices identified by collection_ids even without high_quality_base_model_ids
					const isMacedonianVoice = voice.collection_ids?.includes(
						MACEDONIAN_COLLECTION_ID
					)
					const isCostaRicaVoice = voice.collection_ids?.includes(
						COSTA_RICA_COLLECTION_ID
					)
					const isMalaysiaVoice = voice.collection_ids?.includes(
						MALAYSIA_COLLECTION_ID
					)
					if (!isMacedonianVoice && !isCostaRicaVoice && !isMalaysiaVoice) {
						return false
					}
				}

				// Some voices are weirdly flagged and not working with the sharing option
				if (!voice.sharing) {
					return true
				}

				// Pre-filter to avoid unavailable voices
				return voice.sharing.status !== 'copied_disabled'
			}) || []

		return {
			data: filteredVoices,
			status: 200,
			statusText: 'OK',
			headers: {},
			config: config || {},
		} as AxiosResponse
	},

	updateCampaign(
		id: number,
		data: any,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.put(`/my/announcements/${id}`, data, config)
	},

	deleteCampaign(
		id: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.delete(`/my/announcements/${id}`, config)
	},

	createCampaign(
		data: any,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post('/my/announcements', data, config)
	},

	// Group campaign operations
	getGroupCampaigns(
		groupId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/my/groups/${groupId}/campaigns`, config)
	},

	updateGroupCampaigns(
		groupId: number,
		data: { announcements: number[] },
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.put(`/my/groups/${groupId}/campaigns`, data, config)
	},

	addCampaignToGroup(
		groupId: number,
		data: { announcements: number[] },
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post(`/my/groups/${groupId}/announcements`, data, config)
	},

	removeCampaignFromGroup(
		groupId: number,
		data: { announcements: number[] },
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.delete(`/my/groups/${groupId}/announcements`, {
			...config,
			data,
		})
	},
}
